import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Copy, CheckCircle, Mail, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const { register } = useAuth();
  const navigate     = useNavigate();

  const [step, setStep]       = useState('form');   // 'form' | 'verify' | 'mnemonic'
  const [form, setForm]       = useState({ email:'', password:'', username:'', country:'', role:'buyer' });
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(false);

  // Email verification
  const [sentCode, setSentCode]   = useState('');   // the 6-digit code we generated
  const [inputCode, setInputCode] = useState('');
  const [resending, setResending] = useState(false);
  const [pendingData, setPendingData] = useState(null); // store registration data until verified

  // Mnemonic step
  const [mnemonic, setMnemonic]           = useState('');
  const [mnemonicSaved, setMnemonicSaved] = useState(false);

  const COUNTRIES = [
    'Nigeria','Ghana','Kenya','South Africa','Ethiopia','Tanzania','Uganda','Egypt','Senegal','Rwanda',
    'China','India','Japan','South Korea','Indonesia','Thailand','Vietnam','Philippines','Malaysia','Singapore',
    'United States','Canada','United Kingdom','Germany','France','Netherlands','Spain','Italy','Sweden','Norway',
    'Brazil','Mexico','Argentina','Colombia','Chile','Australia','New Zealand','UAE','Saudi Arabia','Turkey',
  ];

  // Generate a random 6-digit code and "send" it
  const sendVerificationCode = (email) => {
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setSentCode(code);
    // In a real app this would call an email API. Here we show it in a toast for testnet.
    toast(
      (t) => (
        <div>
          <p className="font-bold text-sm mb-1">📧 Verification code sent to</p>
          <p className="text-xs text-gray-300 mb-2">{email}</p>
          <p className="text-xs text-gray-400 mb-1">Testnet mode — your code is:</p>
          <p className="font-mono text-2xl font-bold text-orange-400 tracking-widest">{code}</p>
          <button onClick={() => { navigator.clipboard.writeText(code); toast.dismiss(t.id); toast.success('Code copied!'); }}
            className="mt-2 text-xs text-blue-400 underline">Copy code</button>
        </div>
      ),
      { duration: 30000, style:{ background:'#12121A', color:'#E0E0F0', border:'1px solid #1E1E2E' } }
    );
    return code;
  };

  // Step 1 — submit form → send verification email
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      // Check if email/username already taken before sending code
      // (mockApi will throw on duplicate — we catch and show error)
      // We store form data and move to verify step
      const code = sendVerificationCode(form.email);
      setSentCode(code);
      setPendingData(form);
      setStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — verify the code → actually register
  const handleVerify = async () => {
    if (inputCode.trim() !== sentCode) {
      toast.error('Incorrect code. Check your email or resend.');
      return;
    }
    setLoading(true);
    try {
      const data = await register(pendingData);
      toast.success('✅ Email verified! Account created.');
      if (data.walletMnemonic) {
        setMnemonic(data.walletMnemonic);
        setStep('mnemonic');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = () => {
    setResending(true);
    const code = sendVerificationCode(pendingData.email);
    setSentCode(code);
    setInputCode('');
    setTimeout(() => setResending(false), 1500);
  };

  // ── Mnemonic step ──────────────────────────────────────────────────────────
  if (step === 'mnemonic') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-16">
        <div className="w-full max-w-md">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ background:'rgba(255,200,0,0.1)', border:'1px solid rgba(255,200,0,0.3)' }}>
              <AlertTriangle className="text-yellow-400 flex-shrink-0" size={20} />
              <div>
                <p className="font-bold text-yellow-300 text-sm">Save Your Recovery Phrase!</p>
                <p className="text-yellow-200 text-xs mt-0.5">This is shown ONLY ONCE. If you lose it, you lose access to your wallet forever.</p>
              </div>
            </div>
            <div className="p-4 rounded-xl mb-4 font-mono text-sm leading-relaxed" style={{ background:'#0A0A0F', border:'1px solid #FF6B00', color:'#F5A623' }}>
              {mnemonic}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(mnemonic); toast.success('Seed phrase copied!'); }}
              className="btn-outline w-full flex items-center justify-center gap-2 text-sm py-2 mb-4">
              <Copy size={14} /> Copy Recovery Phrase
            </button>
            <label className="flex items-start gap-3 cursor-pointer mb-4">
              <input type="checkbox" checked={mnemonicSaved} onChange={e => setMnemonicSaved(e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-500" />
              <span className="text-sm text-brand-muted">I have saved my recovery phrase in a safe place and understand I cannot recover it if lost.</span>
            </label>
            <button disabled={!mnemonicSaved} onClick={() => navigate('/')} className="btn-primary w-full flex items-center justify-center gap-2">
              <CheckCircle size={16} /> Continue to CryptoMart
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Verify email step ──────────────────────────────────────────────────────
  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-16 bg-grid">
        <div className="absolute inset-0" style={{ background:'radial-gradient(ellipse at 50% 30%, rgba(255,107,0,0.1) 0%, transparent 60%)' }} />
        <div className="w-full max-w-sm relative z-10">

          {/* Icon + header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background:'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
              <Mail size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily:'Sora' }}>Check your email</h1>
            <p className="text-brand-muted text-sm mt-1">We sent a 6-digit code to</p>
            <p className="text-brand-orange font-semibold text-sm mt-0.5">{pendingData?.email}</p>
          </div>

          <div className="glass rounded-2xl p-6 space-y-4">

            {/* Testnet notice */}
            <div className="p-3 rounded-xl text-xs" style={{ background:'rgba(77,162,255,0.08)', border:'1px solid rgba(77,162,255,0.25)' }}>
              <p className="font-semibold text-blue-300 mb-1">🧪 Testnet Mode</p>
              <p className="text-brand-muted">No real email is sent. Your verification code was shown in the notification that appeared. Check the orange toast popup — it shows your 6-digit code.</p>
            </div>

            {/* Code input */}
            <div>
              <label className="block text-sm font-medium text-brand-muted mb-2">Enter 6-digit code</label>
              <input
                className="input-dark text-center text-2xl font-mono tracking-[0.5em] py-4"
                placeholder="• • • • • •"
                maxLength={6}
                value={inputCode}
                onChange={e => setInputCode(e.target.value.replace(/\D/g,''))}
                onKeyDown={e => e.key === 'Enter' && handleVerify()}
                autoFocus
              />
            </div>

            <button onClick={handleVerify} disabled={loading || inputCode.length !== 6}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-40">
              {loading ? <div className="spinner" /> : <><ShieldCheck size={16} /> Verify & Create Account</>}
            </button>

            <div className="text-center">
              <p className="text-xs text-brand-muted mb-2">Didn't get the code?</p>
              <button onClick={resendCode} disabled={resending} className="text-sm text-brand-orange font-semibold hover:underline disabled:opacity-50">
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>

            <button onClick={() => { setStep('form'); setInputCode(''); setSentCode(''); }}
              className="w-full text-center text-xs text-brand-muted hover:text-white transition-colors">
              ← Back to registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-16 bg-grid">
      <div className="absolute inset-0" style={{ background:'radial-gradient(ellipse at 50% 30%, rgba(255,107,0,0.1) 0%, transparent 60%)' }} />
      <div className="w-full max-w-sm relative z-10">

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily:'Sora' }}>Create Account</h1>
          <p className="text-brand-muted text-sm mt-1">Join millions shopping with crypto</p>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-3 glass rounded-2xl p-6">
          {/* Role toggle */}
          <div className="flex rounded-xl overflow-hidden p-1" style={{ background:'#0A0A0F', border:'1px solid #1E1E2E' }}>
            {['buyer','seller'].map(r => (
              <button key={r} type="button" onClick={() => setForm({...form, role:r})}
                className="flex-1 py-2 text-sm font-semibold rounded-lg transition-all capitalize"
                style={{ background:form.role===r?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color:form.role===r?'white':'#6B6B8A' }}>
                {r === 'buyer' ? '🛒 Buyer' : '🏪 Seller'}
              </button>
            ))}
          </div>

          <input type="text" className="input-dark" placeholder="Username" required
            value={form.username} onChange={e => setForm({...form, username:e.target.value.toLowerCase().replace(/\s/g,'')})} />

          <input type="email" className="input-dark" placeholder="Email address" required
            value={form.email} onChange={e => setForm({...form, email:e.target.value})} />

          <div className="relative">
            <input type={show?'text':'password'} className="input-dark pr-10" placeholder="Password (min 8 chars)" required
              value={form.password} onChange={e => setForm({...form, password:e.target.value})} />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <select className="input-dark" required value={form.country} onChange={e => setForm({...form, country:e.target.value})} style={{ appearance:'none' }}>
            <option value="">Select your country</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {form.role === 'seller' && (
            <div className="p-3 rounded-xl text-xs text-brand-muted" style={{ background:'rgba(255,107,0,0.05)', border:'1px solid rgba(255,107,0,0.2)' }}>
              As a seller, you'll need to complete <strong className="text-brand-orange">KYB verification</strong> before listing products.
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading ? <div className="spinner" /> : <><Mail size={15} /> Continue — Verify Email</>}
          </button>

          <p className="text-center text-xs text-brand-muted">
            A verification code will be sent to your email
          </p>
        </form>

        <p className="text-center text-sm text-brand-muted mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-orange font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
